import type { RequestCategory } from "@nlc/shared-types";

import { usePageTitle } from "@/hooks/usePageTitle";

import type { DesignPickerData } from "./types";

import { Header } from "./components/Header";
import { MultiCategoryResults } from "./components/MultiCategoryResults";
import { SubcategoryTabs } from "./components/SubcategoryTabs";
import { SearchExpandedProvider } from "./contexts/search-expanded.context";
import { useGetDesignTypes } from "./hooks/useGetDesignTypes";
import { usePickTypeState } from "./hooks/usePickTypeState";

const CATEGORY_TITLES: Record<string, string> = {
  graphic: "New Graphic Request",
  video: "New Video Request",
  webui: "New Web Request",
};

function getCategoryLabel(categoryIds: RequestCategory[]) {
  const categoryDisplayNames: Record<RequestCategory, string> = {
    graphic: "graphic",
    video: "video",
    webui: "website & UI",
  };
  const [firstId, secondId, thirdId] = categoryIds;
  const first = firstId && categoryDisplayNames[firstId];
  const second = secondId && categoryDisplayNames[secondId];
  const third = thirdId && categoryDisplayNames[thirdId];

  if (!first) return "";
  if (!second) return first;
  if (!third) return `${first} & ${second}`;

  return `${first}, ${second} or ${third}`;
}

export function PickType() {
  const { data } = useGetDesignTypes();
  const {
    category,
    handleClearSearch,
    handleManualCategoryChange,
    handleSearchChange,
    handleSubcategoryChange,
    matchingCategories,
    searchQuery,
    subcategory,
  } = usePickTypeState();

  usePageTitle(CATEGORY_TITLES[category ?? ""] ?? "New Request");

  const designPickerData: DesignPickerData = {
    categories: data.categories,
    designTypes: data.designTypes,
    subcategories: data.subcategories,
  };

  const isMultiMatch = matchingCategories.length >= 2;

  let highlightedCategoryIds: RequestCategory[] = [];

  if (isMultiMatch) {
    highlightedCategoryIds = matchingCategories.map((cat) => cat.id);
  } else if (category) {
    highlightedCategoryIds = [category];
  }

  return (
    <div className="p-4 h-full">
      <div className="mx-auto h-full w-full flex flex-col rounded-xl border border-border-secondary bg-bg-primary overflow-hidden">
        <div className="flex flex-col flex-1 min-h-0 max-lg:overflow-y-auto max-lg:touch-pan-y max-lg:overscroll-contain max-lg:[scrollbar-width:none] max-lg:[&::-webkit-scrollbar]:hidden">
          <Header
            categories={designPickerData.categories}
            categoryLabel={getCategoryLabel(highlightedCategoryIds)}
            highlightedCategories={highlightedCategoryIds}
            onCategoryChange={handleManualCategoryChange}
          />
          <SearchExpandedProvider>
            {isMultiMatch ? (
              <MultiCategoryResults
                categories={matchingCategories}
                designTypes={designPickerData.designTypes}
                onSearchChange={handleSearchChange}
                searchQuery={searchQuery}
                subcategories={designPickerData.subcategories}
              />
            ) : (
              designPickerData.categories.map(
                (cat) =>
                  cat.id === category && (
                    <div
                      className="flex flex-col flex-1 min-h-0 max-lg:flex-none overflow-hidden"
                      key={cat.id}
                    >
                      <SubcategoryTabs
                        categoryId={cat.id}
                        designTypes={designPickerData.designTypes}
                        onClearSearch={handleClearSearch}
                        onSearchChange={handleSearchChange}
                        onSubcategoryChange={handleSubcategoryChange}
                        searchQuery={searchQuery}
                        subcategories={designPickerData.subcategories}
                        subcategory={subcategory}
                      />
                    </div>
                  ),
              )
            )}
          </SearchExpandedProvider>
        </div>
      </div>
    </div>
  );
}
